/**
 * Maps a country ISO 3166-1 alpha-2 code to the basename of its flag in
 * `src/assets/flags`.
 *
 * The flag files are named after the English country name, but the country
 * list comes from `GET /location/country`, which translates `name` with the
 * `x-lang` header. The translated name does not match a file name, so the
 * name cannot be the key. The ISO code is the same in every language.
 *
 * `tests/unit/constants/countryFlags.spec.ts` asserts that every entry here
 * points at a file that exists.
 */
export const FLAG_FILE_BY_COUNTRY_ISO2: Readonly<Record<string, string>> = {
  AD: 'andorra', // Andorra
  AE: 'unitedarabemirates', // United Arab Emirates
  AF: 'afghanistan', // Afghanistan
  AG: 'antigua', // Antigua
  AL: 'albania', // Albania
  AM: 'armenia', // Armenia
  AO: 'angola', // Angola
  AR: 'argentina', // Argentina
  AT: 'austria', // Austria
  AU: 'australia', // Australia
  AZ: 'azerbaijan', // Azerbaijan
  BA: 'bosniaandherzegovina', // Bosnia and Herzegovina
  BB: 'barbados', // Barbados
  BD: 'bangladesh', // Bangladesh
  BE: 'belgium', // Belgium
  BF: 'burkinafaso', // BurkinaFaso
  BG: 'bulgaria', // Bulgaria
  BH: 'bahrain', // Bahrain
  BI: 'burundi', // Burundi
  BJ: 'benin', // Benin
  BN: 'brunei', // Brunei
  BO: 'bolivia', // Bolivia
  BR: 'brazil', // Brazil
  BS: 'bahamas', // Bahamas
  BT: 'bhutan', // Bhutan
  BW: 'botswana', // Botswana
  BY: 'belarus', // Belarus
  BZ: 'belize', // Belize
  CA: 'canada', // Canada
  CD: 'democraticrepublicofthecongo', // Democratic Republic of the Congo
  CF: 'centralafricanrepublic', // Central African Republic
  CG: 'congo', // Congo
  CH: 'switzerland', // Switzerland
  CI: 'cotedivoire', // Côte d'Ivoire
  CL: 'chile', // Chile
  CM: 'cameroon', // Cameroon
  CN: 'china', // China
  CO: 'colombia', // Colombia
  CR: 'costarica', // CostaRica
  CU: 'cuba', // Cuba
  CV: 'caboverde', // Cabo Verde
  CY: 'cyprus', // Cyprus
  CZ: 'czechia', // Czechia
  DE: 'germany', // Germany
  DJ: 'djibouti', // Djibouti
  DK: 'denmark', // Denmark
  DM: 'dominica', // Dominica
  DO: 'dominicanrepublic', // Dominican Republic
  DZ: 'algeria', // Algeria
  EC: 'ecuador', // Ecuador
  EE: 'estonia', // Estonia
  EG: 'egypt', // Egypt
  ER: 'eritrea', // Eritrea
  ES: 'spain', // Spain
  ET: 'ethiopia', // Ethiopia
  FI: 'finland', // Finland
  FJ: 'fiji', // Fiji
  FM: 'micronesia', // Micronesia
  FR: 'france', // France
  GA: 'gabon', // Gabon
  GB: 'unitedkingdom', // United Kingdom
  GD: 'grenada', // Grenada
  GE: 'georgia', // Georgia
  GH: 'ghana', // Ghana
  GM: 'gambia', // Gambia
  GN: 'guinea', // Guinea
  GQ: 'equatorialguinea', // Equatorial Guinea
  GR: 'greece', // Greece
  GT: 'guatemala', // Guatemala
  GW: 'guineabissau', // Guinea-Bissau
  GY: 'guyana', // Guyana
  HN: 'honduras', // Honduras
  HR: 'croatia', // Croatia
  HT: 'haiti', // Haiti
  HU: 'hungary', // Hungary
  ID: 'indonesia', // Indonesia
  IE: 'ireland', // Ireland
  IL: 'israel', // Israel
  IN: 'india', // India
  IQ: 'iraq', // Iraq
  IR: 'iran', // Iran
  IS: 'iceland', // Iceland
  IT: 'italy', // Italy
  JM: 'jamaica', // Jamaica
  JO: 'jordan', // Jordan
  JP: 'japan', // Japan
  KE: 'kenya', // Kenya
  KG: 'kyrgyzstan', // Kyrgyzstan
  KH: 'cambodia', // Cambodia
  KI: 'kiribati', // Kiribati
  KM: 'comoros', // Comoros
  KN: 'saintkittsandnevis', // Saint Kitts and Nevis
  KP: 'northkorea', // North Korea
  KR: 'southkorea', // South Korea
  KW: 'kuwait', // Kuwait
  KZ: 'kazakhstan', // Kazakhstan
  LA: 'laos', // Laos
  LB: 'lebanon', // Lebanon
  LC: 'saintlucia', // Saint Lucia
  LI: 'liechtenstein', // Liechtenstein
  LK: 'srilanka', // Sri Lanka
  LR: 'liberia', // Liberia
  LS: 'lesotho', // Lesotho
  LT: 'lithuania', // Lithuania
  LU: 'luxembourg', // Luxembourg
  LV: 'latvia', // Latvia
  LY: 'libya', // Libya
  MA: 'morocco', // Morocco
  MC: 'monaco', // Monaco
  MD: 'moldova', // Moldova
  ME: 'montenegro', // Montenegro
  MG: 'madagascar', // Madagascar
  MH: 'marshallislands', // Marshall Islands
  MK: 'northmacedonia', // North Macedonia
  ML: 'mali', // Mali
  MM: 'myanmar', // Myanmar
  MN: 'mongolia', // Mongolia
  MR: 'mauritania', // Mauritania
  MT: 'malta', // Malta
  MU: 'mauritius', // Mauritius
  MV: 'maldives', // Maldives
  MW: 'malawi', // Malawi
  MX: 'mexico', // Mexico
  MY: 'malaysia', // Malaysia
  MZ: 'mozambique', // Mozambique
  NA: 'namibia', // Namibia
  NE: 'niger', // Niger
  NG: 'nigeria', // Nigeria
  NI: 'nicaragua', // Nicaragua
  NL: 'netherlands', // Netherlands
  NO: 'norway', // Norway
  NP: 'nepal', // Nepal
  NR: 'nauru', // Nauru
  NZ: 'newzealand', // New Zealand
  OM: 'oman', // Oman
  PA: 'panama', // Panama
  PE: 'peru', // Peru
  PG: 'papuanewguinea', // Papua New Guinea
  PH: 'philippines', // Philippines
  PK: 'pakistan', // Pakistan
  PL: 'poland', // Poland
  PS: 'palestinestate', // Palestine State
  PT: 'portugal', // Portugal
  PW: 'palau', // Palau
  PY: 'paraguay', // Paraguay
  QA: 'qatar', // Qatar
  RO: 'romania', // Romania
  RS: 'serbia', // Serbia
  RU: 'russia', // Russia
  RW: 'rwanda', // Rwanda
  SA: 'saudiarabia', // Saudi Arabia
  SB: 'solomonislands', // Solomon Islands
  SC: 'seychelles', // Seychelles
  SD: 'sudan', // Sudan
  SE: 'sweden', // Sweden
  SG: 'singapore', // Singapore
  SI: 'slovenia', // Slovenia
  SK: 'slovakia', // Slovakia
  SL: 'sierraleone', // Sierra Leone
  SM: 'sanmarino', // San Marino
  SN: 'senegal', // Senegal
  SO: 'somalia', // Somalia
  SR: 'suriname', // Suriname
  SS: 'southsudan', // South Sudan
  ST: 'saotomeandprincipe', // Sao Tome and Principe
  SV: 'elsalvador', // El Salvador
  SY: 'syria', // Syria
  SZ: 'eswatini', // Eswatini
  TD: 'chad', // Chad
  TG: 'togo', // Togo
  TH: 'thailand', // Thailand
  TJ: 'tajikistan', // Tajikistan
  TL: 'timorleste', // Timor-Leste
  TM: 'turkmenistan', // Turkmenistan
  TN: 'tunisia', // Tunisia
  TO: 'tonga', // Tonga
  TR: 'turkey', // Turkey
  TT: 'trinidadandtobago', // Trinidad and Tobago
  TV: 'tuvalu', // Tuvalu
  TZ: 'tanzania', // Tanzania
  UA: 'ukraine', // Ukraine
  UG: 'uganda', // Uganda
  US: 'unitedstates', // United States
  UY: 'uruguay', // Uruguay
  UZ: 'uzbekistan', // Uzbekistan
  VA: 'holysee', // Holy See
  VC: 'saintvincentandthegrenadines', // Saint Vincent and the Grenadines
  VE: 'venezuela', // Venezuela
  VN: 'vietnam', // Vietnam
  VU: 'vanuatu', // Vanuatu
  WS: 'samoa', // Samoa
  YE: 'yemen', // Yemen
  ZA: 'southafrica', // South Africa
  ZM: 'zambia', // Zambia
  ZW: 'zimbabwe' // Zimbabwe
};

export function getCountryFlagFile(
  countryIso2?: string | null
): string | undefined {
  if (!countryIso2) return undefined;

  return FLAG_FILE_BY_COUNTRY_ISO2[countryIso2.toUpperCase()];
}
